import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  PLATFORM_ID,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { COVERAGE_EVENTS } from '../../data/events.data';
import { CoverageEvent } from '../../models/event.model';
import { PillButtonComponent } from '../../shared/ui/pill-button/pill-button.component';

const MOBILE_MAX_WIDTH = 1023;
const MOBILE_QUERY = `(max-width: ${MOBILE_MAX_WIDTH}px)`;
const PAUSE_AFTER_TOUCH_MS = 10_000;
const AUTO_SCROLL_SPEED = 0.4;
const DRAG_THRESHOLD_PX = 6;
const LOOP_SET_COUNT = 3;

type CarouselItem = CoverageEvent & { trackKey: string };

@Component({
  selector: 'app-event-carousel',
  standalone: true,
  imports: [PillButtonComponent],
  templateUrl: './event-carousel.component.html',
  styleUrl: './event-carousel.component.scss',
})
export class EventCarouselComponent implements AfterViewInit, OnDestroy {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  readonly events: CoverageEvent[] = COVERAGE_EVENTS;

  private readonly viewportRef = viewChild<ElementRef<HTMLElement>>('viewport');
  private readonly trackRef = viewChild<ElementRef<HTMLElement>>('track');

  readonly mobileCarousel = signal(false);
  private readonly trackTransform = signal('');

  private offsetPx = 0;
  private setWidthPx = 0;
  private rafId = 0;
  private resumeTimer: ReturnType<typeof setTimeout> | null = null;
  private paused = false;
  private dragging = false;
  private dragMoved = false;
  private dragStartX = 0;
  private dragStartOffset = 0;
  private measured = false;

  private mediaQuery?: MediaQueryList;
  private onMediaChange?: () => void;
  private resizeObserver?: ResizeObserver;

  readonly loopItems = computed<CarouselItem[]>(() => {
    if (!this.mobileCarousel()) {
      return this.events.map((event) => ({ ...event, trackKey: event.id }));
    }

    return Array.from({ length: LOOP_SET_COUNT }, (_, setIndex) =>
      this.events.map((event) => ({
        ...event,
        trackKey: `${event.id}-${setIndex}`,
      })),
    ).flat();
  });

  readonly transformStyle = computed(() => {
    const transform = this.trackTransform();
    return transform ? { transform } : null;
  });

  ngAfterViewInit(): void {
    if (!this.isBrowser) {
      return;
    }

    this.mediaQuery = window.matchMedia(MOBILE_QUERY);
    this.mobileCarousel.set(this.mediaQuery.matches);
    this.onMediaChange = () => this.handleBreakpointChange();
    this.mediaQuery.addEventListener('change', this.onMediaChange);

    this.bindMeasurements();
    this.startAutoScroll();
  }

  ngOnDestroy(): void {
    if (!this.isBrowser) {
      return;
    }

    this.mediaQuery?.removeEventListener('change', this.onMediaChange!);
    this.resizeObserver?.disconnect();
    this.clearResumeTimer();
    cancelAnimationFrame(this.rafId);
  }

  onCarouselPointerDown(event: PointerEvent): void {
    if (!this.mobileCarousel() || event.button > 0) {
      return;
    }

    this.pauseCarousel();
    this.dragging = true;
    this.dragMoved = false;
    this.dragStartX = event.clientX;
    this.dragStartOffset = this.offsetPx;
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
  }

  onCarouselPointerMove(event: PointerEvent): void {
    if (!this.dragging) {
      return;
    }

    const delta = event.clientX - this.dragStartX;
    if (!this.dragMoved && Math.abs(delta) < DRAG_THRESHOLD_PX) {
      return;
    }

    this.dragMoved = true;
    this.offsetPx = this.dragStartOffset + delta;
    this.normalizeOffset();
    this.applyTransform();
  }

  onCarouselPointerUp(event: PointerEvent): void {
    if (!this.dragging) {
      return;
    }

    this.dragging = false;
    (event.currentTarget as HTMLElement).releasePointerCapture(event.pointerId);
    this.scheduleResume();
  }

  onCarouselPointerCancel(event: PointerEvent): void {
    this.onCarouselPointerUp(event);
  }

  private bindMeasurements(): void {
    const track = this.trackRef()?.nativeElement;
    if (!track) {
      return;
    }

    const measure = () => this.updateMeasurements();

    this.resizeObserver = new ResizeObserver(measure);
    this.resizeObserver.observe(track);
    measure();
  }

  private updateMeasurements(): void {
    const track = this.trackRef()?.nativeElement;
    if (!track || !this.mobileCarousel()) {
      this.setWidthPx = 0;
      this.trackTransform.set('');
      return;
    }

    const firstCard = track.querySelector<HTMLElement>('.event-card');
    if (!firstCard) {
      return;
    }

    const gap = Number.parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 28;
    const cardWidth = firstCard.getBoundingClientRect().width;
    const nextSetWidth = (cardWidth + gap) * this.events.length;

    if (nextSetWidth <= 0) {
      return;
    }

    this.setWidthPx = nextSetWidth;

    if (!this.measured) {
      this.offsetPx = -this.setWidthPx;
      this.measured = true;
      this.applyTransform();
    }
  }

  private handleBreakpointChange(): void {
    const isMobile = this.mediaQuery?.matches ?? false;
    this.mobileCarousel.set(isMobile);

    if (!isMobile) {
      this.paused = false;
      this.dragging = false;
      this.measured = false;
      this.offsetPx = 0;
      this.clearResumeTimer();
      this.trackTransform.set('');
      return;
    }

    this.measured = false;
    this.updateMeasurements();
  }

  private startAutoScroll(): void {
    const tick = () => {
      if (this.mobileCarousel() && !this.paused && !this.dragging && this.setWidthPx > 0) {
        this.offsetPx -= AUTO_SCROLL_SPEED;
        this.normalizeOffset();
        this.applyTransform();
      }

      this.rafId = requestAnimationFrame(tick);
    };

    this.rafId = requestAnimationFrame(tick);
  }

  private normalizeOffset(): void {
    if (this.setWidthPx <= 0) {
      return;
    }

    while (this.offsetPx > 0) {
      this.offsetPx -= this.setWidthPx;
    }

    while (this.offsetPx <= -this.setWidthPx * (LOOP_SET_COUNT - 1)) {
      this.offsetPx += this.setWidthPx;
    }
  }

  private applyTransform(): void {
    if (!this.mobileCarousel()) {
      this.trackTransform.set('');
      return;
    }

    this.trackTransform.set(`translate3d(${this.offsetPx}px, 0, 0)`);
  }

  private pauseCarousel(): void {
    this.paused = true;
    this.clearResumeTimer();
  }

  private scheduleResume(): void {
    this.clearResumeTimer();
    this.resumeTimer = setTimeout(() => {
      this.paused = false;
      this.resumeTimer = null;
    }, PAUSE_AFTER_TOUCH_MS);
  }

  private clearResumeTimer(): void {
    if (this.resumeTimer) {
      clearTimeout(this.resumeTimer);
      this.resumeTimer = null;
    }
  }
}
